import { PropsWithChildren, useEffect, useRef, useState } from "react";

import { cn } from "nazare-ui/utils";

import styles from "./styles/reveal-on-scroll.module.css";

/**
 * Wraps `children` in a fade-up reveal that fires once the element scrolls
 * into view — root defaults to the viewport (`null`), which correctly
 * accounts for a nested `overflow: auto` scroll ancestor (e.g. the shell's
 * `.content`) too, so no explicit `scrollRootRef` plumbing is needed here
 * unlike the contained-table `useInfiniteRows`/`useScrollEdges` hooks. Fires
 * ONCE (`observer.disconnect()` after first intersection) — scrolling back
 * up past an already-revealed item doesn't re-hide it, which would read as
 * flickery rather than a one-time entrance.
 */
export function RevealOnScroll({
  children,
  className,
  delayMs = 0,
}: PropsWithChildren<{ className?: string; delayMs?: number }>) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -60px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={cn(styles.reveal, visible && styles.visible, className)}
      style={delayMs ? { transitionDelay: `${delayMs}ms` } : undefined}
    >
      {children}
    </div>
  );
}
